import { isValidElement, type ReactNode } from 'react';
import * as s from './ValidationMessage.css';

export type ValidationLevel = 'error' | 'warning' | 'info';

export interface ValidationMessageProps {
  level: ValidationLevel;
  message: ReactNode;
  /** form field input id — caller 가 input 의 aria-describedby 에 연결한다. */
  fieldId?: string;
  className?: string;
}

/**
 * form field 옆 inline validation 메시지. error 는 role='alert' (assertive),
 * warning/info 는 role='status' (polite). multiline ReactNode 는 flex column.
 *
 * - message='' 이면 렌더 안 함 (CSS :empty fallback 도 보호).
 * - fieldId 가 있으면 element 의 id 를 `${fieldId}-validation` 로 설정.
 *   caller 는 input 에 aria-describedby={`${fieldId}-validation`} 추가.
 */
export function ValidationMessage({ level, message, fieldId, className }: ValidationMessageProps) {
  if (message === '' || message === null || message === undefined) return null;

  const role = level === 'error' ? 'alert' : 'status';
  const ariaLive = level === 'error' ? 'assertive' : 'polite';

  // multiline detection — ReactNode 가 element (ul, ol, div, span 등) 면
  // flex-direction: column 적용. 단순 string 이면 inline (default).
  const isMultiline = isValidElement(message);

  return (
    <span
      id={fieldId ? `${fieldId}-validation` : undefined}
      role={role}
      aria-live={ariaLive}
      aria-atomic="true"
      className={`${s.root} ${s.level[level]}${isMultiline ? ` ${s.multiline}` : ''}${className ? ` ${className}` : ''}`}
    >
      {message}
    </span>
  );
}
